/**
 * Verifica se um valor é válido (não `undefined`, `null` ou string vazia).
 *
 * @param value - O valor a ser validado.
 * @returns `true` se o valor for não-vazio, `false` caso contrário.
 *
 * @example
 * isValidValue("João"); // true
 */
export function isValidValue(value: string | undefined | null): boolean {
  return value !== undefined && value !== null && value !== "";
}

/**
 * Processa um template HTML com placeholders Mustache (`{{var}}`, `{{#var}}...{{/var}}`).
 *
 * Suporta:
 * - `{{variavel}}` — substituição simples
 * - `{{#variavel}}...{{/variavel}}` — bloco condicional (exibido somente se `variavel` tiver valor)
 *
 * @param template - A string do template HTML.
 * @param data - Objeto chave-valor para substituição dos placeholders.
 * @returns A string HTML renderizada.
 *
 * @example
 * ```typescript
 * const html = renderTemplate("<p>{{name}}</p>", { name: "João" });
 * // "<p>João</p>"
 * ```
 */
export function renderTemplate(template: string, data: Record<string, any>): string {
  let result = template;

  // 1. Blocos condicionais {{#var}}...{{/var}} (loop para suportar aninhamento)
  let previous = "";
  while (result !== previous) {
    previous = result;
    result = result.replace(
      /\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g,
      (_match, key, content) => {
        const value = data[key];
        return isValidValue(value) ? content : "";
      }
    );
  }

  // 2. Substituições simples {{variavel}}
  result = result.replace(/\{\{(\w+)\}\}/g, (_match, key) => {
    const value = data[key];
    return isValidValue(value) ? value : "";
  });

  return result;
}
